/**
 * @module M/control/AddServicesControl
 */

import AddServicesImplControl from 'impl/addservicescontrol';
import template from 'templates/addservices';
import resultstemplate from 'templates/addservicesresults';

export default class AddServicesControl extends M.Control {
  /**
   * @classdesc
   * Main constructor of the class. Creates a PluginControl
   * control
   *
   * @constructor
   * @extends {M.Control}
   * @api stable
   */
  constructor(values) {
    // 1. checks if the implementation can create PluginControl
    if (M.utils.isUndefined(AddServicesImplControl)) {
      M.exception('La implementación usada no puede crear controles AddServicesControl');
    }
    // 2. implementation of this control
    const impl = new AddServicesImplControl();
    super(impl, 'AddServices');

    this.stateSelectAll = false;
    this.http = values.http;
    this.https = values.https;

    /**
     * Set con los nombres de las capas WMS añadidas a través de este plugin.
     * Se usa para identificarlas en el DOM del layerswitcher.
     * @private
     * @type {Set}
     */
    this.addedLayerNames_ = new Set();

    /**
     * MutationObserver para detectar re-renders del layerswitcher
     * y volver a aplicar el grupo "Capas cargadas".
     * @private
     * @type {MutationObserver|null}
     */
    this.lsObserver_ = null;

    /**
     * Panel sobre el que está instalado lsObserver_, para detectar que
     * Mapea ha sustituido el nodo del panel y reinstalar el observer.
     * @private
     * @type {HTMLElement|null}
     */
    this.lsPanelObserved_ = null;

    /**
     * Timer de reintento para cuando el panel del layerswitcher todavía
     * no existe en el DOM al añadir la primera capa.
     * @private
     * @type {number|null}
     */
    this.lsTimer_ = null;

    /**
     * Flag para evitar bucles infinitos del MutationObserver cuando
     * somos nosotros mismos quienes modificamos el DOM.
     * @private
     * @type {boolean}
     */
    this.applyingGroup_ = false;
  }

  /**
   * This function adds events to HTML buttons
   *
   * @function
   * @private
   * @param {HTMLElement} html - HTML template
   */
  addEvents(html) {
    this.urlService = html.querySelector('#m-addservices-search-input');
    this.btnAddUrl = html.querySelector('#m-addservices-search-btn');
    this.btnClear = html.querySelector('#m-addservices-clear-btn');
    this.containerAddservices = html;
    this.containerSearching = html.querySelector('#m-searching-result-addservices');
    this.containerResults = html.querySelector('#m-addservices-results');
    this.containerSearch = html.querySelector('#m-addservices-search');
    this.btnAddUrl.addEventListener('click', evt => this.readCapabilities(evt));
    this.btnClear.addEventListener('click', evt => this.removeContains(evt));
  }

  /**
   * This function creates the view
   *
   * @public
   * @function
   * @param {M.Map} map to add the control
   * @api stable
   */
  createView(map) {
    this.facadeMap = map;
    return new Promise((success, fail) => {
      const html = M.template.compileSync(template);
      this.addEvents(html);

      // Escuchar el evento ADDED_WMS del mapa para saber cuándo se ha
      // añadido una capa WMS y reorganizar el layerswitcher.
      // El usuario garantiza que la única vía para añadir WMS tras la
      // carga del mapa es este plugin, por lo que el evento es equivalente
      // a "capa añadida por addservices".
      this.facadeMap.on(M.evt.ADDED_WMS, () => {
        this.scheduleGroupUpdate_();
      });

      success(html);
    });
  }

  /**
   * This function reads service capabilities
   *
   * @function
   * @private
   * @param {Event} evt - Click event
   */
  readCapabilities(evt) {
    evt.preventDefault();
    let HTTPeval = false;
    let HTTPSeval = false;
    if (!M.utils.isNullOrEmpty(this.urlService.value)) {
      if (M.utils.isUrl(this.urlService.value)) {
        if (this.http && !this.https) {
          const expReg = /^(http:)/;
          HTTPeval = expReg.test(this.urlService.value.trim());
        } else if (this.https && !this.http) {
          const expReg = /^(https:)/;
          HTTPSeval = expReg.test(this.urlService.value.trim());
        } else if (this.http && this.https) {
          HTTPeval = true;
          HTTPSeval = true;
        }

        if (HTTPeval === true || HTTPSeval === true) {
          this.containerAddservices.classList.add('m-searching');
          M.remote.get(M.utils.getWMSGetCapabilitiesUrl(this.urlService.value)).then((response) => {
            try {
              const getCapabilitiesParser = new M.impl.format.WMSCapabilities();
              const getCapabilities = getCapabilitiesParser.read(response.xml);
              const getCapabilitiesUtils = new M.impl.GetCapabilities(
                getCapabilities,
                this.urlService.value,
                this.facadeMap.getProjection().code,
              );
              this.capabilities = getCapabilitiesUtils.getLayers();
              this.showResults();
            } catch (err) {
              M.dialog.error('Ha ocurrido un error al obtener el documento GetCapabilities.');
            }
            this.containerAddservices.classList.remove('m-searching');
          });
        } else {
          let errorMsg;
          if (this.http) {
            errorMsg = 'Sólo se permiten servicios http';
          } else if (this.https) {
            errorMsg = 'Sólo se permiten servicios https';
          } else if (!this.http && !this.https) {
            errorMsg = 'No se permiten servicios http ni https';
          }
          M.dialog.error(errorMsg);
        }
      } else {
        M.dialog.error('Debes indicar una URL válida.');
      }
    } else {
      M.dialog.error('Los campos están vacíos.');
    }
  }

  /**
   * This function shows results
   *
   * @function
   * @private
   */
  showResults() {
    const result = [];
    this.capabilities.forEach((capability) => {
      result.push(capability.getImpl());
    });

    const html = M.template.compileSync(resultstemplate, {
      vars: {
        result,
      },
    });

    this.containerResults.innerHTML = html.innerHTML;
    M.utils.enableTouchScroll(this.containerResults);
    const results = this.containerResults.getElementsByTagName('span');
    for (let i = 0; i < results.length; i += 1) {
      results[i].addEventListener('click', evt => this.registerCheck(evt));
    }
    this.containerResults.querySelector('#m-addservices-selectall').addEventListener('click', evt => this.registerCheck(evt));
    this.containerResults.querySelector('.m-addservices-add').addEventListener('click', evt => this.addLayers(evt));
  }

  /**
   * This function registers the marks or unmarks check and click allselect
   *
   * @function
   * @private
   * @param {Event} evt - Event
   */
  registerCheck(evt) {
    const e = (evt || window.event);
    if (!M.utils.isNullOrEmpty(e.target) && e.target.classList.contains('m-check-addservices')) {
      e.stopPropagation();
      e.target.classList.toggle('g-cartografia-check2');
      e.target.classList.toggle('g-cartografia-check3');
      if (!this.containerResults.querySelectorAll('.g-cartografia-check3').length) {
        this.stateSelectAll = false;
      } else if (!this.containerResults.querySelectorAll('.g-cartografia-check2').length) {
        this.stateSelectAll = true;
      }
    } else if (!M.utils.isNullOrEmpty(e.target) && e.target.id === 'm-addservices-selectall') {
      if (this.stateSelectAll) {
        this.unSelect();
        this.stateSelectAll = false;
      } else {
        this.select();
        this.stateSelectAll = true;
      }
    }
  }

  /**
   * This function unselects checkboxs
   *
   * @function
   * @private
   */
  unSelect() {
    const unSelect = this.containerResults.querySelectorAll('.g-cartografia-check3');
    for (let i = 0; i < unSelect.length; i += 1) {
      unSelect[i].classList.remove('g-cartografia-check3');
      unSelect[i].classList.add('g-cartografia-check2');
    }
  }

  /**
   * This function selects checkboxs
   *
   * @function
   * @private
   */
  select() {
    const select = this.containerResults.querySelectorAll('.g-cartografia-check2');
    for (let i = 0; i < select.length; i += 1) {
      select[i].classList.remove('g-cartografia-check2');
      select[i].classList.add('g-cartografia-check3');
    }
  }

  /**
   * This function adds layers
   *
   * @function
   * @param {Event} evt - Event
   * @private
   */
  addLayers(evt) {
    evt.preventDefault();
    const layers = [];
    const elmSel = this.containerResults.querySelectorAll('.g-cartografia-check3');
    if (elmSel.length === 0) {
      M.dialog.error('Debes seleccionar al menos una capa.');
    } else {
      for (let i = 0; i < elmSel.length; i += 1) {
        for (let j = 0; j < this.capabilities.length; j += 1) {
          if (elmSel[i].id === this.capabilities[j].legend.replace(/ /g, '')) {
            this.capabilities[j].options.origen = 'WMS';
            layers.push(this.capabilities[j]);
          }
        }
      }

      // Registrar los nombres antes de añadir al mapa, para que cuando
      // M.evt.ADDED_WMS se dispare ya estén en el Set.
      layers.forEach((layer) => {
        const name = (layer.name || layer.legend || '').trim();
        if (name) this.addedLayerNames_.add(name);
      });

      this.facadeMap.addLayers(layers);
      // La reorganización del layerswitcher se dispara vía M.evt.ADDED_WMS
    }
  }

  /**
   * Lanza la actualización del layerswitcher tras cada M.evt.ADDED_WMS.
   * Se aplica de forma inmediata; el MutationObserver se encarga de los
   * re-renders posteriores del panel. El reintento diferido sólo cubre el
   * caso en que el panel del layerswitcher aún no está en el DOM.
   *
   * @function
   * @private
   */
  scheduleGroupUpdate_() {
    this.setupLayerswitcherObserver_();
    this.applyCapasCargadasGroup_();

    if (this.lsTimer_) clearTimeout(this.lsTimer_);
    if (!this.lsPanelObserved_) {
      this.lsTimer_ = setTimeout(() => {
        this.lsTimer_ = null;
        this.scheduleGroupUpdate_();
      }, 300);
    }
  }

  /**
   * Descarta del Set los nombres de capas que ya no están en el mapa,
   * de modo que el grupo sólo se oculta cuando el usuario ha eliminado
   * realmente las capas y no mientras el layerswitcher se re-renderiza.
   *
   * @function
   * @private
   */
  syncAddedLayerNames_() {
    if (this.addedLayerNames_.size === 0 || !this.facadeMap) return;
    if (typeof this.facadeMap.getWMS !== 'function') return;

    const onMap = new Set();
    this.facadeMap.getWMS().forEach((layer) => {
      const name = (layer.name || layer.legend || '').trim();
      if (name) onMap.add(name);
    });

    Array.from(this.addedLayerNames_).forEach((name) => {
      if (!onMap.has(name)) this.addedLayerNames_.delete(name);
    });
  }

  /**
   * Crea (si no existe) y rellena el grupo "Capas cargadas" en el
   * layerswitcher, situándolo entre "Capas base" y "Otras capas".
   * Las capas identificadas en addedLayerNames_ se mueven desde
   * ul.m-layers a este nuevo grupo.
   * El grupo sólo se retira del DOM cuando ninguna de sus capas sigue
   * en el mapa, nunca durante un re-render del panel.
   *
   * Estructura del layerswitcher de Mapea 6.7 (ID: m-layerswitcher-panel):
   *   #m-layerswitcher-panel > ul
   *     li.m-group  → "Capas base"  (div.m-title + ul.m-base-layers)
   *     li.m-group  → "Otras capas" (div.m-title + ul.m-layers)
   *
   * @function
   * @private
   */
  applyCapasCargadasGroup_() {
    if (this.applyingGroup_) return;
    this.applyingGroup_ = true;

    try {
      this.syncAddedLayerNames_();

      // ── 1. Localizar el panel del layerswitcher ─────────────────────────
      const lsPanel = document.querySelector('#m-layerswitcher-panel');
      if (!lsPanel) return;

      // Si ya no queda ninguna capa del plugin en el mapa, retirar el grupo
      if (this.addedLayerNames_.size === 0) {
        const stale = lsPanel.querySelector('li.m-group.m-capas-cargadas');
        if (stale) stale.parentNode.removeChild(stale);
        return;
      }

      const rootUl = lsPanel.querySelector('ul');
      if (!rootUl) return;

      // ── 2. Localizar el li.m-group de "Otras capas" ────────────────────
      // Buscamos entre los hijos directos para no confundir con
      // posibles sub-grupos internos.
      const allGroups = Array.from(rootUl.children);
      let otrasCapasLi = null;
      allGroups.forEach((li) => {
        if (!li.classList.contains('m-group')) return;
        const titleEl = li.querySelector('.m-title');
        if (titleEl && titleEl.textContent.trim() === 'Otras capas') {
          otrasCapasLi = li;
        }
      });
      if (!otrasCapasLi) return;

      // ── 3. Encontrar o crear el li.m-group de "Capas cargadas" ──────────
      let capasLi = rootUl.querySelector('li.m-group.m-capas-cargadas');
      if (!capasLi) {
        capasLi = document.createElement('li');
        capasLi.className = 'm-group m-capas-cargadas';

        const titleDiv = document.createElement('div');
        titleDiv.className = 'm-title';
        titleDiv.textContent = 'Capas cargadas';

        const layersList = document.createElement('ul');
        layersList.className = 'm-layers m-capas-cargadas-list';

        capasLi.appendChild(titleDiv);
        capasLi.appendChild(layersList);

        // Insertar justo ANTES del li de "Otras capas"
        rootUl.insertBefore(capasLi, otrasCapasLi);
      }

      // ── 4. Mover las capas WMS añadidas al nuevo grupo ──────────────────
      const capasUl = capasLi.querySelector('ul');
      const otrasUl = otrasCapasLi.querySelector('ul.m-layers');
      if (!otrasUl || !capasUl) return;

      const addedNamesArr = Array.from(this.addedLayerNames_);

      // Iterar sobre los hijos directos de ul.m-layers para identificar
      // las capas que son de este plugin y moverlas.
      Array.from(otrasUl.children).forEach((li) => {
        const text = li.textContent.trim();
        const matches = addedNamesArr.some(name => text.indexOf(name) !== -1);
        if (matches) {
          capasUl.appendChild(li);
        }
      });
    } finally {
      // Descartar las mutaciones provocadas por nosotros mismos para que el
      // observer no se vuelva a disparar en cadena.
      if (this.lsObserver_) this.lsObserver_.takeRecords();
      this.applyingGroup_ = false;
      this.setupLayerswitcherObserver_();
    }
  }

  /**
   * Configura un MutationObserver sobre el layerswitcher para detectar
   * si se re-renderiza (el panel de Mapea reconstruye su DOM al
   * abrirse/cerrarse) y re-aplicar el grupo "Capas cargadas".
   *
   * El callback re-aplica el grupo de forma SÍNCRONA: los callbacks de
   * MutationObserver se ejecutan antes del siguiente repintado, por lo que
   * el usuario nunca llega a ver un fotograma sin el grupo. Cualquier
   * aplazamiento (setTimeout) provoca el parpadeo.
   *
   * @function
   * @private
   */
  setupLayerswitcherObserver_() {
    const lsPanel = document.querySelector('#m-layerswitcher-panel');
    if (!lsPanel) return;
    if (this.lsObserver_ && this.lsPanelObserved_ === lsPanel && lsPanel.isConnected) {
      return;
    }
    if (this.lsObserver_) this.lsObserver_.disconnect();

    this.lsPanelObserved_ = lsPanel;
    this.lsObserver_ = new MutationObserver(() => {
      if (this.applyingGroup_) return;
      this.applyCapasCargadasGroup_();
    });

    this.lsObserver_.observe(lsPanel, { childList: true, subtree: true });
  }

  /**
   * This function remove results show
   *
   * @function
   * @param {goog.events.BrowserEvent} evt - Event
   * @private
   */
  removeContains(evt) {
    evt.preventDefault();
    this.containerResults.innerHTML = '';
    this.urlService.value = '';
  }

  /**
   * This function compares controls
   *
   * @public
   * @function
   * @param {M.Control} control to compare
   */
  equals(control) {
    return control instanceof AddServicesControl;
  }
}
